import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, eur, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, PackageCheck, ChevronDown, FileDown, Mail } from "lucide-react";

const STATUS = ["bozza", "inviato", "accettato", "rifiutato", "ordine"];

export default function Preventivi() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lineFor, setLineFor] = useState(null);
  const [convertFor, setConvertFor] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/quotes").then(({ data }) => setRows(data));

  useEffect(() => {
    const nuovo = params.get("nuovo");
    load().then(() => { if (nuovo) setExpanded(nuovo); });
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/users", { params: { role: "tecnico" } }).then(({ data }) => setTechs(data)).catch(() => {});
    api.get("/price-lists").then(({ data }) => setPriceLists(data)).catch(() => {});
  }, []);

  const quoteFields = [
    { name: "title", label: "Oggetto", required: true, full: true },
    {
      name: "customer_id", label: "Cliente esistente", type: "select",
      options: customers.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: "lead_id", label: "Oppure lead", type: "select",
      options: leads.map((l) => ({ value: l.id, label: l.company })),
    },
    {
      name: "price_list_id", label: "Listino da applicare", type: "select",
      options: priceLists.map((p) => ({ value: p.id, label: `${p.name} (−${p.discount_pct || 0}%)` })),
    },
    { name: "valid_until", label: "Valido fino al", type: "date" },
    { name: "discount_pct", label: "Sconto %", type: "number" },
    { name: "status", label: "Stato", type: "select", options: STATUS.map((s) => ({ value: s, label: s })) },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const lineFields = [
    {
      name: "product_id", label: "Articolo da catalogo", type: "select", full: true,
      options: products.map((p) => ({ value: p.id, label: `${p.code} — ${p.name} (${eur(p.price)})` })),
    },
    { name: "name", label: "Descrizione (se fuori catalogo)", full: true },
    {
      name: "kind", label: "Tipo", type: "select",
      options: [
        { value: "vendita", label: "Vendita" },
        { value: "noleggio", label: "Noleggio (canone mensile)" },
        { value: "servizio", label: "Servizio" },
      ],
    },
    { name: "quantity", label: "Quantità", type: "number" },
    { name: "unit_price", label: "Prezzo unitario €", type: "number" },
  ];

  const convertFields = [
    { name: "scheduled_date", label: "Data installazione", type: "date" },
    { name: "scheduled_time", label: "Ora", type: "time" },
    {
      name: "technician_id", label: "Tecnico", type: "select", full: true,
      options: techs.map((t) => ({ value: t.id, label: t.name })),
    },
    {
      name: "create_contract", label: "Genera anche il contratto di manutenzione", type: "switch", full: true,
      hint: "Crea un contratto attivo sull'impianto e le relative scadenze di manutenzione",
    },
    { name: "contract_frequency_months", label: "Frequenza manutenzione (mesi)", type: "number" },
    { name: "contract_months", label: "Durata contratto (mesi)", type: "number" },
    { name: "contract_amount", label: "Importo annuo contratto €", type: "number" },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/quotes/${editing.id}`, values);
      else await api.post("/quotes", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const addLine = async (values) => {
    try {
      await api.post(`/quotes/${lineFor.id}/lines`, values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const convert = async (values) => {
    try {
      const { data } = await api.post(`/quotes/${convertFor.id}/convert`, values);
      let text = `Ordine creato: impianto ${data.plant.code}, intervento ${data.intervention.code}.`;
      if (data.contract) {
        text += ` Contratto ${data.contract.code} attivo, ${data.maintenance_created} scadenze di manutenzione generate.`;
      }
      setMsg(text);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const downloadPdf = async (q) => {
    try {
      const { data } = await api.get(`/quotes/${q.id}/quote.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `preventivo-${q.code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg("Download del PDF non riuscito.");
    }
  };

  const sendQuote = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/send`);
      setMsg(`Preventivo ${q.code} inviato a ${data.sent_to} con il PDF allegato via link.`);
      await load();
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="preventivi-page">
      <PageHeader title="Preventivi" subtitle={`${rows.length} preventivi`}>
        <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }}
                data-testid="new-quote-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuovo preventivo
        </Button>
      </PageHeader>

      {msg && <p className="mb-4 text-sm font-medium text-primary" data-testid="quote-message">{msg}</p>}

      <div className="space-y-4" data-testid="preventivi-list">
        {rows.map((q) => (
          <Card key={q.id} className="rounded-sm shadow-none overflow-hidden">
            <button
              type="button"
              className="w-full text-left px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
              onClick={() => setExpanded(expanded === q.id ? null : q.id)}
              data-testid={`expand-quote-${q.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{q.code}</span>
                  <p className="font-semibold">{q.title}</p>
                  <Badge className="rounded-sm capitalize" variant={q.status === "ordine" ? "default" : "secondary"}>
                    {q.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {q.customer_name || q.lead_name || "Nessun destinatario"} · valido fino al {itDate(q.valid_until)} ·
                  {q.lines?.length || 0} voci{q.price_list_name ? ` · listino ${q.price_list_name}` : ""}
                  {q.discount_pct ? ` · sconto ${q.discount_pct}%` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display text-xl font-black tracking-tighter">{eur(q.totals?.total)}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded === q.id ? "rotate-180" : ""}`} />
              </div>
            </button>

            {expanded === q.id && (
              <div className="border-t border-border px-4 sm:px-6 py-4" data-testid={`quote-detail-${q.id}`}>
                <div className="divide-y divide-border">
                  {(q.lines || []).map((l) => (
                    <div key={l.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <div>
                        <p className="font-medium">{l.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {l.kind} · {l.quantity} × {eur(l.unit_price)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono">{eur(l.quantity * l.unit_price)}</span>
                        <Button variant="ghost" size="icon" className="text-destructive"
                                onClick={async () => { await api.delete(`/quote-lines/${l.id}`); load(); }}
                                data-testid={`delete-line-${l.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(q.lines || []).length === 0 && (
                    <p className="py-2 text-sm text-muted-foreground">Nessuna voce inserita.</p>
                  )}
                </div>
                <div className="flex justify-between text-sm mt-4 pt-3 border-t border-border">
                  <span className="text-muted-foreground">Subtotale / sconto</span>
                  <span className="font-mono">{eur(q.totals?.subtotal)} − {eur(q.totals?.discount)}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" className="rounded-sm" onClick={() => setLineFor(q)}
                          data-testid={`add-line-${q.id}`}>
                    <Plus className="h-3.5 w-3.5 mr-2" /> Aggiungi voce
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => downloadPdf(q)}
                          data-testid={`quote-pdf-${q.id}`}>
                    <FileDown className="h-3.5 w-3.5 mr-2" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => sendQuote(q)}
                          data-testid={`send-quote-${q.id}`}>
                    <Mail className="h-3.5 w-3.5 mr-2" /> Invia al cliente
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm"
                          onClick={() => { setEditing(q); setOpen(true); }} data-testid={`edit-quote-${q.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" className="rounded-sm" disabled={q.status === "ordine"}
                          onClick={() => setConvertFor(q)} data-testid={`convert-quote-${q.id}`}>
                    <PackageCheck className="h-3.5 w-3.5 mr-2" />
                    {q.status === "ordine" ? "Già ordinato" : "Trasforma in ordine"}
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                          onClick={async () => { await api.delete(`/quotes/${q.id}`); load(); }}
                          data-testid={`delete-quote-${q.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
        {rows.length === 0 && <ListCard><Empty text="Nessun preventivo. Creane uno da un lead o da un cliente." /></ListCard>}
      </div>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica preventivo" : "Nuovo preventivo"}
                  fields={quoteFields} initial={editing || { status: "bozza" }}
                  onSubmit={save} testid="quote-dialog" />
      <FormDialog open={!!lineFor} onOpenChange={(v) => !v && setLineFor(null)} title="Aggiungi voce"
                  fields={lineFields} initial={{ quantity: 1, kind: "vendita" }} onSubmit={addLine}
                  testid="line-dialog" />
      <FormDialog open={!!convertFor} onOpenChange={(v) => !v && setConvertFor(null)}
                  title="Trasforma in ordine" fields={convertFields}
                  initial={{
                    scheduled_time: "09:00", contract_frequency_months: 6, contract_months: 12,
                    contract_amount: convertFor?.totals?.total || 0,
                  }}
                  onSubmit={convert} testid="convert-dialog" />
    </div>
  );
}
